import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getMandates,
  startMandateSetup,
  revokeMandate,
  type Mandate,
} from "../../services/payments";
import { useFeatures } from "../../hooks/useFeatures";

/**
 * Automatic payment, and the card it runs on.
 *
 * For a family that always intended to pay, being chased six times a month is
 * the worst part of the product. This removes it — and the only way that is
 * acceptable is if the parent stays in charge: they see which card, for which
 * club, when it was last used, and they can stop it in one tap.
 *
 * One card per club, because with direct charges the card is stored on the
 * club's own Stripe account. Said out loud rather than left to look like a
 * bug when a parent with two clubs is asked twice.
 */
export default function AutoPayCard({
  clubs,
}: {
  /** Clubs this parent owes money to, so a card can be added for one. */
  clubs: { club_id: string; club_name: string; can_pay_online: boolean }[];
}) {
  const { features } = useFeatures();
  const [mandates, setMandates] = useState<Mandate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMandates(await getMandates());
    } catch {
      setMandates([]);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mandate") === "success") {
      toast.success("Cardul a fost salvat. De acum plătim automat la scadență.");
      load();
    } else if (params.get("mandate") === "cancel") {
      toast("Nu am salvat niciun card.", { icon: "ℹ️" });
    }
  }, [load]);

  const add = async (clubId: string) => {
    setBusy(clubId);
    try {
      const { url } = await startMandateSetup(clubId, window.location.pathname);
      window.location.href = url;
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut deschide salvarea cardului.");
      setBusy(null);
    }
  };

  const stop = async (m: Mandate) => {
    if (!window.confirm(`Oprești plata automată pentru ${m.club_name}? Cardul salvat se șterge.`)) return;
    setBusy(m.id);
    try {
      await revokeMandate(m.id);
      toast.success("Plata automată a fost oprită.");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut opri plata automată.");
    }
    setBusy(null);
  };

  // Standing permission to charge a card is its own switch: a platform can
  // have online payment live and still not be ready to debit people.
  if (!features.paymentsAutoDebit) return null;

  if (!loaded) return null;

  const payableClubs = clubs.filter((c) => c.can_pay_online);
  const withoutCard = payableClubs.filter((c) => !mandates.some((m) => m.club_id === c.club_id));

  if (mandates.length === 0 && withoutCard.length === 0) return null;

  return (
    <div className="p-4 bg-white border shadow-sm border-slate-200 rounded-2xl">
      <h3 className="text-sm font-extrabold text-slate-800">Plată automată</h3>
      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
        Achităm singuri la scadență, din cardul salvat. Te anunțăm cu 3 zile înainte de fiecare
        debitare și poți opri oricând.
      </p>

      {mandates.length > 0 && (
        <div className="mt-3 divide-y divide-slate-100">
          {mandates.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-800">{m.club_name}</p>
                <p className="text-xs text-slate-500">
                  {[m.card_brand, m.card_last4 ? `•••• ${m.card_last4}` : null]
                    .filter(Boolean)
                    .join(" ") || "Card salvat"}
                  {m.status === "paused" ? " · oprit după refuzuri repetate" : ""}
                </p>
                {m.last_charged_at && (
                  <p className="text-[11px] text-slate-400">
                    Ultima debitare: {new Date(m.last_charged_at).toLocaleDateString("ro-RO")}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {m.status === "paused" && (
                  <button
                    type="button"
                    onClick={() => add(m.club_id)}
                    disabled={busy !== null}
                    className="px-3 py-2 text-xs font-bold text-white bg-indigo-600 rounded-xl disabled:opacity-50"
                  >
                    Salvează alt card
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => stop(m)}
                  disabled={busy !== null}
                  className="px-3 py-2 text-xs font-bold bg-white border text-slate-600 border-slate-200 rounded-xl disabled:opacity-50"
                >
                  {busy === m.id ? "..." : "Oprește"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {withoutCard.map((c) => (
        <button
          key={c.club_id}
          type="button"
          onClick={() => add(c.club_id)}
          disabled={busy !== null}
          className="w-full px-4 py-3 mt-3 text-sm font-bold transition-colors border border-indigo-200 text-indigo-700 rounded-xl hover:bg-indigo-50 disabled:opacity-50"
        >
          {busy === c.club_id ? "Se deschide..." : `Activează plata automată pentru ${c.club_name}`}
        </button>
      ))}

      {mandates.length > 0 && (
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
          Cardul se păstrează la fiecare club separat, pentru că plata merge direct la club. Datele
          cardului le ține Stripe — nu trec prin aplicație.
        </p>
      )}
    </div>
  );
}
